import { ImageCropModal } from '@/components/cropper/ImageCropModal';
import DatePicker from '@/components/forms/DatePicker';
import Input from '@/components/forms/Input';
import RadioGroup from '@/components/forms/RadioGroup';
import { Upload } from '@/components/upload';
import { useDynamicLocale } from '@/hoslog-intl';
import { FileObject } from '@/lib/uploadToAWS';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AffiliateFormValues } from '../affiliate-form.mapper';

type Props = { defaultImageUrl?: string };

export const ProfileSection = ({ defaultImageUrl = '' }: Props) => {
  const form = useFormContext<AffiliateFormValues>();
  const { t } = useDynamicLocale();
  const [previewUrl, setPreviewUrl] = useState(defaultImageUrl);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [isCropOpen, setIsCropOpen] = useState(false);
  const [uploadKey, setUploadKey] = useState(0);

  useEffect(() => setPreviewUrl(defaultImageUrl), [defaultImageUrl]);
  useEffect(
    () => () => {
      if (cropSource?.startsWith('blob:')) URL.revokeObjectURL(cropSource);
    },
    [cropSource],
  );

  const genderOptions = useMemo(
    () => [
      { value: 'F', label: t('affiliate.page.affiliateInfoForm.female') },
      { value: 'M', label: t('affiliate.page.affiliateInfoForm.male') },
      { value: 'O', label: t('affiliate.page.affiliateInfoForm.others') },
    ],
    [t],
  );

  const closeCropper = () => {
    setIsCropOpen(false);
    setCropSource(null);
    setUploadKey((key) => key + 1);
  };

  return (
    <>
      <div className="grid w-full gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        <div className="h-[200px] w-full md:col-span-2 md:max-w-[308px] pl-12.5 md:mx-auto lg:col-span-1 lg:max-w-none lg:mx-0">
          <Controller
            name="image"
            control={form.control}
            render={({ field, fieldState }) => (
              <Upload
                key={uploadKey}
                variant="circle"
                accept="image/*"
                multiple={false}
                maxSizeMB={5}
                defaultPreviewUrl={previewUrl}
                previewClassName="h-[164px] w-[164px]"
                error={fieldState.error?.message}
                onFilesChange={(files) => {
                  const selected = files?.[0];
                  if (!selected || typeof selected === 'string') return;
                  setCropSource(selected.preview || URL.createObjectURL(selected.file as File));
                  setIsCropOpen(true);
                }}
                onDefaultPreviewRemove={() => {
                  setPreviewUrl('');
                  field.onChange(undefined);
                }}
              />
            )}
          />
        </div>
        <div className="grid grid-cols-2 col-span-2 gap-4">
          <Input
            type="text"
            disabled
            label="affiliate.page.affiliateInfoForm.userName.label"
            {...form.register('userName')}
            forceRequired
          />
          <div />
          <Input
            type="text"
            label="affiliate.page.affiliateInfoForm.fullName.label"
            placeholder={t('affiliate.page.affiliateInfoForm.fullName.placeholder')}
            {...form.register('fullName')}
            maxLength={50}
            forceRequired
          />
          <Input
            type="text"
            label="affiliate.page.affiliateInfoForm.furigana.label"
            placeholder={t('common.form.placeholder.furiganaPlaceholder')}
            {...form.register('affiliateNameFurigana')}
            maxLength={50}
            forceRequired
          />
          <Input
            type="text"
            disabled
            label="affiliate.page.affiliateInfoForm.mobileNumber.label"
            {...form.register('mobileNumber')}
            forceRequired
          />
          <Input
            type="text"
            disabled
            label="affiliate.page.affiliateInfoForm.email.label"
            {...form.register('emailAddress')}
            forceRequired
          />
          <DatePicker label="affiliate.page.affiliateInfoForm.dob.label" name="dob" require18Years forceRequired />
          <RadioGroup
            groupLabel={t('affiliate.page.affiliateInfoForm.gender.label')}
            name="gender"
            label="affiliate.page.affiliateInfoForm.gender.label"
            options={genderOptions}
            className="flex flex-col"
            forceRequired
          />
        </div>
      </div>
      <ImageCropModal
        open={isCropOpen}
        imageSrc={cropSource}
        cropShape="round"
        onClose={closeCropper}
        onCropDone={(croppedFile, croppedPreviewUrl) => {
          const image: FileObject = { file: croppedFile, id: `${Date.now()}`, preview: croppedPreviewUrl };
          setPreviewUrl(croppedPreviewUrl);
          form.setValue('image', image, { shouldValidate: true, shouldDirty: true });
          closeCropper();
        }}
      />
    </>
  );
};
